import type { RegraAutomacao } from '@pgcustom/shared-types';
import type { Resultado } from '../../core/resultado.js';
import type { PgTable, PgTableColumn, PgTableRow, PgKpiCard } from '@pgcustom/design-system';
import type { AutomacoesApi } from './automacoes.api.js';
import type { PermissoesAutomacoes } from './automacoes.permissoes.js';
import { formatarDataHora } from '../pedidos/formatacao.js';

const COLUNAS: PgTableColumn[] = [
  { key: 'nome', label: 'Regra' },
  { key: 'descricao', label: 'Descrição' },
  { key: 'status', label: 'Status' },
  { key: 'atualizadoEm', label: 'Última alteração' },
];

/**
 * `<erp-automacoes-page>` — Automações (Sprint 13, `ROADMAP.md`/
 * `PRODUCT.md` 3.12, `DECISIONS.md` DEC-032). Conjunto FIXO de 5 regras
 * reais e documentadas (`migrations/018`) — não um motor de regras
 * genérico configurável, nenhuma UI de "compor uma condição arbitrária"
 * existe aqui de propósito. Clique numa linha liga/desliga a regra
 * (mesmo padrão de "ação real direta no clique de linha" de
 * `<erp-garantias-page>`/`<erp-envios-page>`).
 */
export class ErpAutomacoesPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _automacoesApi: AutomacoesApi | null = null;
  private _permissoes: PermissoesAutomacoes = { ler: false, atualizar: false };

  private resultadoRegras: Resultado<RegraAutomacao[]> = { status: 'carregando' };
  private regrasAtuais: RegraAutomacao[] = [];
  private mensagemAcao = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set automacoesApi(valor: AutomacoesApi) {
    this._automacoesApi = valor;
    if (this.isConnected) void this.carregar();
  }

  set permissoes(valor: PermissoesAutomacoes) {
    this._permissoes = valor;
    if (this.isConnected) this.renderEsqueleto();
  }

  connectedCallback(): void {
    this.renderEsqueleto();
    if (this._automacoesApi) void this.carregar();
  }

  private renderEsqueleto(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        .topo { margin-bottom: var(--pg-space-5, 24px); }
        h1 { font-family: var(--pg-font-familia-display, system-ui); font-size: var(--pg-font-size-xl, 1.5rem); color: var(--pg-color-texto-primario, #f2f3f5); margin: 0 0 4px 0; }
        .subtitulo { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); margin: 0; }
        .dica { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-desabilitado, #5c626b); margin-top: var(--pg-space-3, 12px); }
        .erro-acao { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-erro, #e5484d); margin-top: var(--pg-space-3, 12px); }
        .erro-acao:empty { display: none; }
        .kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-4, 16px); }
      </style>
      <div class="topo">
        <h1>Automações</h1>
        <p class="subtitulo">Regras reais que notificam automaticamente — nenhuma delas precisa de configuração além de ligar/desligar.</p>
      </div>

      <div class="kpis">
        <pg-kpi-card id="kpi-ativas" label="Ativas" icone="automacoes" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-desativadas" label="Desativadas" icone="automacoes" estado="carregando"></pg-kpi-card>
      </div>

      <pg-card padding="compacto">
        <div id="regras"></div>
      </pg-card>
      ${this._permissoes.atualizar ? '<p class="dica">Clique em uma regra para ligar ou desligar.</p>' : ''}
      <p class="erro-acao">${escaparHtml(this.mensagemAcao)}</p>
    `;

    this.root.querySelector('#regras')?.addEventListener('pg-row-click', (evento) => {
      if (!this._permissoes.atualizar) return;
      const { index } = (evento as CustomEvent<{ row: PgTableRow; index: number }>).detail;
      const regra = this.regrasAtuais[index];
      if (regra) void this.alternarAtiva(regra);
    });

    this.atualizar();
  }

  private async alternarAtiva(regra: RegraAutomacao): Promise<void> {
    if (!this._automacoesApi) return;
    try {
      await this._automacoesApi.atualizarAtiva(regra.chave, !regra.ativa);
      this.mensagemAcao = '';
      void this.carregar();
    } catch {
      this.mensagemAcao = 'não foi possível atualizar a regra. tente novamente.';
      this.renderEsqueleto();
    }
  }

  private async carregar(): Promise<void> {
    const api = this._automacoesApi;
    if (!api) return;

    if (!this._permissoes.ler) {
      this.resultadoRegras = { status: 'sem-permissao' };
      this.atualizar();
      return;
    }

    this.resultadoRegras = { status: 'carregando' };
    this.atualizar();
    this.resultadoRegras = await api.listarRegras();
    this.renderEsqueleto();
  }

  private atualizar(): void {
    const container = this.root.querySelector('#regras');
    if (!container) return;

    if (this.resultadoRegras.status === 'ok') {
      this.regrasAtuais = this.resultadoRegras.dado;
      container.innerHTML = '<pg-table></pg-table>';
      const tabela = container.querySelector('pg-table') as PgTable;
      tabela.columns = COLUNAS;
      tabela.rows = this.regrasAtuais.map((r) => ({
        nome: r.nome,
        descricao: r.descricao,
        status: r.ativa ? { texto: 'Ativa', tom: 'sucesso' as const } : { texto: 'Desativada', tom: 'neutro' as const },
        atualizadoEm: formatarDataHora(r.atualizadoEm),
      }));
      const ativas = this.regrasAtuais.filter((r) => r.ativa).length;
      (this.root.querySelector('#kpi-ativas') as PgKpiCard | null)?.setAttribute('estado', 'ok');
      (this.root.querySelector('#kpi-ativas') as PgKpiCard | null)?.setAttribute('valor', String(ativas));
      (this.root.querySelector('#kpi-desativadas') as PgKpiCard | null)?.setAttribute('estado', 'ok');
      (this.root.querySelector('#kpi-desativadas') as PgKpiCard | null)?.setAttribute('valor', String(this.regrasAtuais.length - ativas));
      return;
    }

    this.regrasAtuais = [];
    const { variant, titulo, descricao } = estadoVazio(this.resultadoRegras);
    container.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
    for (const id of ['kpi-ativas', 'kpi-desativadas']) {
      const kpi = this.root.querySelector(`#${id}`) as PgKpiCard | null;
      if (!kpi) continue;
      if (this.resultadoRegras.status === 'vazio') {
        kpi.setAttribute('estado', 'ok');
        kpi.setAttribute('valor', '0');
      } else {
        kpi.setAttribute('estado', variant === 'carregando' ? 'carregando' : 'indisponivel');
      }
    }
  }
}

function estadoVazio(resultado: Resultado<RegraAutomacao[]>): { variant: string; titulo: string; descricao: string } {
  switch (resultado.status) {
    case 'carregando':
      return { variant: 'carregando', titulo: 'Automações', descricao: '' };
    case 'sem-permissao':
      return { variant: 'sem-permissao', titulo: 'Automações', descricao: 'você não tem permissão para ver este dado' };
    case 'erro':
      return { variant: 'erro', titulo: 'Automações', descricao: resultado.mensagem };
    default:
      return { variant: 'vazio', titulo: 'Nenhuma regra', descricao: '' };
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-automacoes-page', ErpAutomacoesPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-automacoes-page': ErpAutomacoesPage;
  }
}
